import { type ApiResult, getJson, patchJson, postJson } from "@/api/client"
import {
  mentionHandleGroupPath,
  mentionHandleGroupsPath,
  verifyMentionHandlePath,
} from "@/routes"
import type {
  MentionHandleGroup,
  MentionHandleVerificationStatus,
  Platform,
} from "@/types"

type MentionHandlesAttributes = Record<
  number,
  { id?: number; platform_id: number; handle: string }
>

export interface MentionHandleGroupPayload {
  label: string
  fallback_text?: string
  mention_handles_attributes: MentionHandlesAttributes
}

// Convert a name-keyed handle map into the index-keyed
// `mention_handles_attributes` payload Rails expects, preserving the existing
// handle's id (when editing a group) so updates map to the right records.
export function buildMentionHandlesAttributes(
  mentionPlatforms: Platform[],
  handles: Record<string, string>,
  existingGroup?: MentionHandleGroup,
): MentionHandlesAttributes {
  const attributes: MentionHandlesAttributes = {}
  mentionPlatforms.forEach((platform, idx) => {
    const existing = existingGroup?.mention_handles.find(
      (h) => h.platform.name === platform.name,
    )
    attributes[idx] = {
      ...(existing ? { id: existing.id } : {}),
      platform_id: existing?.platform.id ?? platform.id,
      handle: handles[platform.name] ?? "",
    }
  })
  return attributes
}

export function verifyMentionHandle(
  platform: string,
  handle: string,
): Promise<ApiResult<{ status: MentionHandleVerificationStatus }>> {
  return getJson(verifyMentionHandlePath({ platform, handle, format: "json" }))
}

export function saveMentionHandleGroup(
  payload: MentionHandleGroupPayload,
  existingId?: number,
): Promise<ApiResult<MentionHandleGroup>> {
  const body = { mention_handle_group: payload }

  return existingId != null
    ? patchJson(mentionHandleGroupPath(existingId, { format: "json" }), body)
    : postJson(mentionHandleGroupsPath({ format: "json" }), body)
}
